import React, { useState, useRef, useEffect } from 'react';
import { Upload, Video, Youtube, Loader, X, AlertTriangle, CheckCircle, Save, List, Play, HelpCircle } from 'lucide-react';
import { analyzeVideo, saveVideoAnalytics, getVideoAnalyticsByPlayerId, deleteVideoAnalytics, analyzeMultipleVideos } from '../utils/videoAnalyticsApi';
import { useTheme } from '../utils/theme';
import { errorMessage, successMessage } from '../utils/helpers';
import FolderDropdown from './FolderDropdown';
import YouTubeErrorDisplay, { YouTubeRestrictionsTooltip } from './YouTubeErrorDisplay';

const VideoAnalyticsModal = ({ isOpen, onClose, playerId, fetchExistingVideos = () => { } }) => {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark
  const [inputType, setInputType] = useState('upload');
  const [videoFiles, setVideoFiles] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('Analyze this video');
  const [analysisResult, setAnalysisResult] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDownloadingYoutube, setIsDownloadingYoutube] = useState(false);
  const [error, setError] = useState(null);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [analysisTitle, setAnalysisTitle] = useState('');
  const [playerIdForAnalysis, setPlayerIdForAnalysis] = useState(playerId || '');
  const [savedAnalytics, setSavedAnalytics] = useState([]);
  const [showSavedAnalytics, setShowSavedAnalytics] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const fileInputRef = useRef(null);

  // Fetch saved analytics for this player when component mounts or playerId changes
  useEffect(() => {
    if (playerId) {
      fetchSavedAnalytics(playerId);
      setPlayerIdForAnalysis(playerId);
    }
  }, [playerId]);

  // Fetch saved analytics for a specific player
  const fetchSavedAnalytics = async (id) => {
    try {
      const analytics = await getVideoAnalyticsByPlayerId(id);
      setSavedAnalytics(analytics);
    } catch (error) {
      console.error('Error fetching saved analytics:', error);
      // No need to show an error to the user for this background fetch
    }
  };

  // Handle video analysis
  const handleAnalyzeVideo = async () => {
    if (!selectedFolderId?.length) {
      return errorMessage('Please select a folder before uploading a video')
    }
    // Reset states
    setIsAnalyzing(true);
    setIsDownloadingYoutube(inputType === 'youtube');
    setAnalysisResult('');
    setError(null);

    try {
      // Validate inputs
      if (inputType === 'upload' && videoFiles.length === 0) {
        throw new Error('Please upload a video file');
      }
      if (inputType === 'youtube' && !youtubeUrl) {
        throw new Error('Please enter a valid YouTube URL');
      }

      // Validate YouTube URL format if that's the input type
      if (inputType === 'youtube') {
        // Check basic YouTube URL format
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
        if (!youtubeRegex.test(youtubeUrl)) {
          throw new Error('Please enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=...)');
        }

        // Ensure URL is properly formatted
        if (!youtubeUrl.startsWith('http')) {
          // Add https:// if not present
          setYoutubeUrl(`https://${youtubeUrl}`);
        }
      }

      // Call the API
      let result;
      if (inputType === 'upload') {

        if (videoFiles?.length > 1) {
          // Use multiple video analysis
          result = await analyzeMultipleVideos({
            videoFiles,
            instructions: additionalInstructions,
            analysisTitle: analysisTitle,
            folderId: selectedFolderId
          });
          // Join results with a separator
          // setAnalysisResult(result.join('\n\n--- Next Video Analysis ---\n\n'));
        } else {
          // Use single video analysis
          result = await analyzeVideo({
            videoFile: videoFiles[0],
            youtubeUrl: null,
            instructions: additionalInstructions,
            analysisTitle: analysisTitle,
            folderId: selectedFolderId
          });
          // setAnalysisResult(result);
        }
      } else {
        // YouTube analysis
        result = await analyzeVideo({
          videoFile: null,
          youtubeUrl: youtubeUrl,
          instructions: additionalInstructions,
          folderId: selectedFolderId
        });
        // setAnalysisResult(result);
      }
      fetchExistingVideos()
      onClose()
      resetForm()
      setIsAnalyzing(false);
      setVideoFiles([])
      successMessage('Upload started. Your videos are being processed.');

      // Set a default title based on timestamp
      // const now = new Date();
      // setAnalysisTitle(`Video Analysis - ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`);

    } catch (error) {
      console.error('Analysis error:', error);
      // Store YouTube errors with special flag for enhanced display
      if (inputType === 'youtube' && error.message) {
        setError({
          isYouTube: true,
          message: error.message
        });
      } else {
        setError(error.message || 'An unexpected error occurred');
      }
      setAnalysisResult('');
    } finally {
      setIsAnalyzing(false);
      setIsDownloadingYoutube(false);
    }
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const validPreviews = [];

    files.forEach(file => {
      if (!file.type.startsWith('video/')) {
        setError('One or more files are not valid video files');
        return;
      }
      validFiles.push(file);
      validPreviews.push(URL.createObjectURL(file));
    });

    setVideoFiles(prevFiles => [...prevFiles, ...validFiles]);
    setVideoPreviews(prevPreviews => [...prevPreviews, ...validPreviews]);
    if (validFiles.length > 0) {
      setError(null);
    }
  };

  // Remove a video from the list
  const handleRemoveVideo = (index) => {
    setVideoFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setVideoPreviews(prevPreviews => {
      // Revoke the URL to prevent memory leaks
      URL.revokeObjectURL(prevPreviews[index]);
      return prevPreviews.filter((_, i) => i !== index);
    });
  };

  // YouTube URL input handler
  const handleYoutubeUrlChange = (e) => {
    setYoutubeUrl(e.target.value);
    // Clear error if user is typing a new URL
    if (error && error.includes('YouTube')) {
      setError(null);
    }
  };

  // Handle saving analysis to database
  const handleSaveAnalysis = async () => {
    setIsSaving(true);

    try {
      if (!analysisResult) {
        throw new Error('No analysis text available to save');
      }

      if (!analysisTitle.trim()) {
        throw new Error('Please provide a title for this analysis');
      }

      // Prepare analysis data
      const analysisData = {
        title: analysisTitle,
        analysis: analysisResult,
        inputType: inputType,
        sourceUrl: inputType === 'youtube' ? youtubeUrl : null,
        instructions: additionalInstructions,
        timestamp: new Date().toISOString(),
        playerId: playerIdForAnalysis || null,
        folderId: selectedFolderId || null
      };

      // Save to database
      const savedData = await saveVideoAnalytics(analysisData);

      // Update local list
      setSavedAnalytics(prev => [savedData, ...prev]);

      // Confirmation
      setError({ type: 'success', message: 'Analysis saved successfully!' });
    } catch (error) {
      console.error('Error saving analysis:', error);
      setError(`Failed to save analysis: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle loading a saved analysis
  const handleLoadAnalysis = (analysis) => {
    setAnalysisResult(analysis.analysis);
    setInputType(analysis.inputType || 'upload');
    if (analysis.inputType === 'youtube' && analysis.sourceUrl) {
      setYoutubeUrl(analysis.sourceUrl);
    }
    setAdditionalInstructions(analysis.instructions || '');
    setAnalysisTitle(analysis.title || '');
    setShowSavedAnalytics(false);
    
    // Set the folder ID if available
    if (analysis.folderId) {
      setSelectedFolderId(analysis.folderId);
    } else {
      setSelectedFolderId(null);
    }
  };

  // Handle deleting a saved analysis
  const handleDeleteAnalysis = async (analysisId, e) => {
    e.stopPropagation(); // Prevent triggering the parent click handler

    if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteVideoAnalytics(analysisId);

      // Update local list
      setSavedAnalytics(prev => prev.filter(a => a.id !== analysisId));

    } catch (error) {
      console.error('Error deleting analysis:', error);
      setError(`Failed to delete analysis: ${error.message || 'Unknown error'}`);
    }
  };

  // Clear current input and results
  const resetForm = () => {
    if (inputType === 'upload') {
      setVideoFiles([]);
      setVideoPreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setYoutubeUrl('');
    }
    setAdditionalInstructions('');
    setAnalysisResult('');
    setError(null);
    setAnalysisTitle('');
    
    // Always reset the folder selection
    setSelectedFolderId(null);
  };

  // Handle backdrop click to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 cursor-pointer" onClick={handleBackdropClick}>
      <div className={`${'bg-gray-800 text-gray-200'} rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col cursor-default`} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b goldenGradient from-blue-600 to-blue-800 text-white rounded-t-lg">
          <h2 className="text-xl font-bold">Video Analysis</h2>
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="text-white hoverintoGray rounded-full p-1"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-3 overflow-y-auto flex-grow">

          {/* Input Controls */}
          <div className="max-w-2xl mx-auto space-y-3">

            {/* Error/Success message display */}
            {error && (
              error.isYouTube ? (
                // Use YouTubeErrorDisplay for YouTube-specific errors
                <YouTubeErrorDisplay
                  errorMessage={error.message}
                  onDismiss={() => setError(null)}
                  showUploadButton={true}
                  onUploadClick={() => setInputType('upload')}
                  compact={true}
                />
              ) : (
                <div className={`p-3 border rounded flex items-start ${error.type === 'success'
                  ? 'bg-green-900 border-green-700 text-green-200'
                  : 'bg-red-900 border-red-700 text-red-200'
                  }`}>
                  {error.type === 'success'
                    ? <CheckCircle size={20} className={`mr-2 mt-0.5 flex-shrink-0 ${'text-green-300'}`} />
                    : <AlertTriangle size={20} className="mr-2 mt-0.5 flex-shrink-0" />
                  }
                  <div className="flex-1">
                    {typeof error === 'string'
                      ? error
                      : error.message
                    }
                  </div>
                  <button
                    onClick={() => setError(null)}
                    className={`ml-2 p-1 rounded hover:bg-black/10 transition-colors ${'text-gray-300'}`}
                    aria-label="Dismiss error"
                  >
                    <X size={16} />
                  </button>
                </div>
              )
            )}
            {/* Saved Analyses Button */}
            <div className="flex justify-between mb-4">
              {/* <button
                onClick={() => setShowSavedAnalytics(!showSavedAnalytics)}
                className="flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <List size={18} className="mr-2" />
                {showSavedAnalytics ? 'Hide Saved Analyses' : `Show Player's Analyses`}
              </button> */}

              {analysisResult && (
                <button
                  onClick={handleSaveAnalysis}
                  disabled={isSaving || !analysisResult}
                  className="flex items-center px-4 py-2 themePrimary text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <Loader size={18} className="animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={18} className="mr-2" />
                      Save
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Saved Analyses List */}
            {showSavedAnalytics && (
              <div className={`border rounded-lg p-4 ${'bg-gray-700 border-gray-600'} mb-4`}>
                <h3 className={`text-lg font-semibold ${'text-blue-400'} mb-3 flex items-center`}>
                  <List size={18} className="mr-2" />
                  Saved Analyses for Player
                </h3>

                {savedAnalytics.length === 0 ? (
                  <div className={`text-center py-6 ${'text-gray-400'}`}>
                    <p>No saved analysis for this player</p>
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto">
                    {savedAnalytics.map((analysis) => (
                      <div
                        key={analysis.id}
                        className={`border-b ${'border-gray-600 hover:bg-gray-600'} last:border-0 py-3 flex justify-between items-center cursor-pointer`}
                        onClick={() => handleLoadAnalysis(analysis)}
                      >
                        <div className="flex-1 pr-4">
                          <div className="font-medium">
                            {analysis.title || 'Untitled Analysis'}
                          </div>
                          <div className={`text-xs ${'text-gray-400'} mt-1`}>
                            {new Date(analysis.timestamp).toLocaleString()}
                          </div>
                        </div>
                        <button
                          onClick={(e) => handleDeleteAnalysis(analysis.id, e)}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Delete analysis"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Input Source Selection */}
            <div className={`border rounded-lg p-3 ${'bg-gray-700 border-gray-600'}`}>
              <h3 className={`text-lg font-semibold ${'text-blue-400'} mb-3 flex items-center`}>
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">1</span>
                Select Input Source
              </h3>

              <div className="flex space-x-4 mb-4">
                <button
                  onClick={() => setInputType('upload')}
                  className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${inputType === 'upload'
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-600 text-gray-300 hover:bg-gray-600'
                    }`}
                >
                  <Upload size={16} className="mr-2" />
                  Upload Video
                </button>

                <button
                  onClick={() => setInputType('youtube')}
                  className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${inputType === 'youtube'
                    ? 'bg-red-50 border-red-500 text-red-700'
                    : 'border-gray-600 text-gray-300 hover:bg-gray-600'
                    }`}
                >
                  <Youtube size={16} className="mr-2" />
                  YouTube URL
                </button>
              </div>

              {/* Video Upload */}
              {inputType === 'upload' ? (
                <div>
                  <div
                    className={`border-2 border-dashed ${'border-gray-500 hover:bg-gray-600'} rounded-lg p-6 text-center cursor-pointer transition-colors`}
                    onClick={() => fileInputRef.current.click()}
                  >
                    <Upload className={`h-10 w-10 mx-auto ${'text-gray-300'} mb-3`} />
                    <p className="text-lg font-medium mb-2">Drag & drop your videos</p>
                    <p className={`${'text-gray-400'} mb-3`}>
                      or <span className="text-blue-600 underline">browse from your device</span>
                    </p>
                    <div className="flex justify-center space-x-4 text-sm">
                      <span className={`px-2 py-1 rounded ${'bg-gray-600 text-gray-300'}`}>MP4</span>
                      <span className={`px-2 py-1 rounded ${'bg-gray-600 text-gray-300'}`}>MOV</span>
                      <span className={`px-2 py-1 rounded ${'bg-gray-600 text-gray-300'}`}>AVI</span>
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="video/*"
                      onChange={handleFileChange}
                      className="hidden"
                      multiple
                    />
                  </div>

                  {videoPreviews.length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center mb-3">
                        <span className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">✓</span>
                        <span className="font-medium">Uploaded Videos ({videoFiles.length})</span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-3">
                        {videoPreviews.map((preview, index) => (
                          <div key={index} className="relative">
                            <video
                              src={preview}
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              onClick={() => handleRemoveVideo(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                              title="Remove video"
                            >
                              <X size={12} />
                            </button>
                            <div className="mt-1 text-xs text-gray-500 truncate">
                              {videoFiles[index]?.name}
                            </div>
                            <div className="text-xs text-gray-400">
                              {(videoFiles[index]?.size / (1024 * 1024)).toFixed(2)} MB
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Status Summary */}
                      <div className="flex justify-between items-center bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-3">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{videoFiles.length}</div>
                          <div className="text-sm text-gray-600">Videos Uploaded</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {(videoFiles.reduce((total, file) => total + file.size, 0) / (1024 * 1024)).toFixed(1)} MB
                          </div>
                          <div className="text-sm text-gray-600">Total Size</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">AI</div>
                          <div className="text-sm text-gray-600">Ready to Analyze</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Youtube className="text-red-500 mr-2" size={20} />
                      <input
                        type="text"
                        placeholder="https://www.youtube.com/watch?v=..."
                        value={youtubeUrl}
                        onChange={handleYoutubeUrlChange}
                        className={`flex-1 p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${'bg-gray-700 border-gray-600 text-white placeholder-gray-400'}`}
                      />
                    </div>

                    {/* YouTube Requirements */}
                    <div className={`border rounded-lg p-3 ${'bg-amber-900/20 border-amber-800'}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className={`font-medium flex items-center gap-2 ${'text-amber-300'}`}>
                          <AlertTriangle size={16} />
                          YouTube Video Requirements
                        </h4>
                        <YouTubeRestrictionsTooltip />
                      </div>
                      <ul className={`text-sm space-y-1.5 ${'text-amber-400'}`}>
                        <li className="flex items-start gap-2">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />
                          <span><strong>Public videos only</strong> - Private/unlisted cannot be analyzed</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />
                          <span><strong>Duration:</strong> Under 10 minutes recommended (max 1 hour)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-amber-500 flex-shrink-0" />
                          <span><strong>No copyright blocks</strong> - Content-claimed videos may fail</span>
                        </li>
                      </ul>
                      <p className={`mt-2 text-xs ${'text-amber-500'}`}>
                        <strong>Tip:</strong> Upload video files directly for best results.
                      </p>
                    </div>

                    {/* Status Summary for YouTube */}
                    <div className="flex justify-between items-center bg-gradient-to-r from-red-50 to-purple-50 rounded-lg p-3">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{youtubeUrl ? '1' : '0'}</div>
                        <div className="text-sm text-gray-600">Videos Ready</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">0 MB</div>
                        <div className="text-sm text-gray-600">Source</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">AI</div>
                        <div className="text-sm text-gray-600">Ready to Analyze</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Analysis Details */}
            <div className={`border rounded-lg p-3 ${'bg-gray-700 border-gray-600'}`}>
              <h3 className={`text-lg font-semibold ${'text-blue-400'} mb-3 flex items-center`}>
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">2</span>
                Analysis Details
              </h3>

              <div className="space-y-3">
                {/* Analysis Title */}
                <div>
                  <label className={`block text-sm font-medium ${'text-gray-300'} mb-1`}>
                    Analysis Title
                  </label>
                  <input
                    type="text"
                    placeholder="Enter a title for this analysis"
                    value={analysisTitle}
                    onChange={(e) => setAnalysisTitle(e.target.value)}
                    className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 ${'bg-gray-700 border-gray-600 text-white placeholder-gray-400'}`}
                  />
                </div>

                {/* Roster ID */}
                <div>
                  <label className={`block text-sm font-medium ${'text-gray-300'} mb-1`}>
                    Player ID (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Select a player (optional)"
                    value={playerIdForAnalysis}
                    onChange={(e) => setPlayerIdForAnalysis(e.target.value)}
                    className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 ${'bg-gray-600 border-gray-600 text-white placeholder-gray-400'}`}
                    readOnly={!!playerId} // Make read-only if playerId was passed as prop
                  />
                </div>
                
                {/* Folder Selection */}
                <FolderDropdown
                  selectedFolderId={selectedFolderId}
                  onFolderSelect={setSelectedFolderId}
                  label="Select or Create Folder"
                />

                {/* Additional Instructions */}
                <div>
                  <label className={`block text-sm font-medium ${'text-gray-300'} mb-1`}>
                    Analysis Instructions (Optional)
                  </label>
                  <textarea
                    placeholder="Analyze this video"
                    className={`w-full p-2 border rounded-md h-20 focus:outline-none focus:ring-2 focus:ring-blue-600 ${'bg-gray-700 border-gray-600 text-white placeholder-gray-400'}`}
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2 mt-4">
                <button
                  onClick={handleAnalyzeVideo}
                  disabled={isAnalyzing || (inputType === 'upload' && videoFiles.length === 0) || (inputType === 'youtube' && !youtubeUrl)}
                  className="flex-1 py-2 px-4 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader size={18} className="animate-spin mr-2" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Play size={18} className="mr-2" />
                      Analyze Video
                    </>
                  )}
                </button>

                <button
                  onClick={resetForm}
                  className={`py-2 px-4 ${'bg-gray-600 text-gray-200 hover:bg-gray-500'} rounded-md focus:outline-none`}
                  disabled={isAnalyzing}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoAnalyticsModal;